
import React, { Component } from 'react';

import { createToolbarEvent, sendAnalytics } from '../../../analytics';
import { translate } from '../../../base/i18n';
import { IconShareDesktop } from '../../../base/icons';
import { isLocalParticipantModerator } from '../../../base/participants';
import { InlineDialogWithoutAnalytics } from '../../../base/react/components/web/InlineDialog';
import { connect } from '../../../base/redux';

import ToolbarButton from './ToolbarButton';

/**
 * The type of the React {@code Component} props of {@link ScreenOverflowButton}.
 */
type Props = {

    /**
     * A child React Element to display within {@code InlineDialog}.
     */
    children: React$Node,

    /**
     * Whether or not the OverflowMenu popover should display.
     */
    isOpen: boolean,

    /**
     * Calback to change the visibility of the overflow menu.
     */
    onVisibilityChange: Function,

    /**
     * Whether to display the OverflowMenu as a drawer.
     */
    overflowDrawer: boolean,

    /**
     * Sharing for Whiteboard.
     */
    _sharingWhiteBoard: Boolean,

    /**
     * Sharing Etherpad.
     */
    _shareEtherpad: Boolean,

    /**     * Whether screen sharing is disabled.
     */
    disabledProps: boolean,

    /**
     * Invoked to obtain translated strings.
     */
    t: Function
};

/**
 * A React {@code Component} for opening or closing the {@code OverflowMenu}.
 *
 * @augments Component
 */
class ScreenOverflowButton extends Component<Props> {
    /**
     * Initializes a new {@code ScreenOverflowButton} instance.
     *
     * @param {Object} props - The read-only properties with which the new
     * instance is to be initialized.
     */
    constructor(props: Props) {
        super(props);

        // Bind event handlers so they are only bound once per instance.
        this._onCloseDialog = this._onCloseDialog.bind(this);
        this._onToggleDialogVisibility
            = this._onToggleDialogVisibility.bind(this);
    }

    /**
     * Implements React's {@link Component#render()}.
     *
     * @inheritdoc
     * @returns {ReactElement}
     */
    render() {
        const { children, isOpen } = this.props;

        return (
            <InlineDialogWithoutAnalytics
                content = { children }
                isOpen = { isOpen }
                onClose = { this._onCloseDialog }
                placement = 'top'>
                {this._renderToolbarButton()}
            </InlineDialogWithoutAnalytics>
        );
    }

    _renderToolbarButton: () => React$Node;

    /**
     * Renders the actual toolbar overflow menu button.
     *
     * @returns {ReactElement}
     */
    _renderToolbarButton() {
        const { t, toggled, _screenShareDisabled, disabledProps } = this.props;

        return (
            <ToolbarButton
                accessibilityLabel =
                    { t('toolbar.accessibilityLabel.moreActions') }
                disabled = { disabledProps || _screenShareDisabled ? 'disabled' : undefined }
                icon = { IconShareDesktop }
                onClick = { this._onToggleDialogVisibility }
                position = { 'top' }
                toggled = { toggled }
                tooltip = { disabledProps || _screenShareDisabled ? t('toolbar.shareDisabled') : t('toolbar.Share') } />
        );
    }

    _onCloseDialog: () => void;

    /**
     * Callback invoked when {@code InlineDialog} signals that it should be
     * close.
     *
     * @private
     * @returns {void}
     */
    _onCloseDialog() {
        this.props.onVisibilityChange(false);
    }

    _onToggleDialogVisibility: () => void;

    /**
     * Callback invoked to signal that an event has occurred that should change
     * the visibility of the {@code InlineDialog} component.
     *
     * @private
     * @returns {void}
     */
    _onToggleDialogVisibility() {
        if (this.props.disabledProps || this.props._screenShareDisabled) {
            return;
        }

        sendAnalytics(createToolbarEvent('overflow'));

        this.props.onVisibilityChange(!this.props.isOpen);
    }
}

/**
 * Maps (parts of) the Redux state to the associated props for the
 * {@code ScreenOverflowButton} component.
 *
 * @param {Object} state - The Redux state.
 * @returns {Props}
 */
function mapStateToProps(state) {
    const { overflowDrawer } = state['features/toolbox'];
    const sharingWhiteBoard = state['features/genericiframe'].visible;
    const { editing: shareEtherpad } = state['features/etherpad'];
    const conferenceView = state['features/base/conference'].conferenceView;
    const screenShareDisabledForGuest = state['features/base/jwt'].confSettings?.DISABLE_SCREEN_SHARING_FOR_GUEST;
    const isPersonModerator = isLocalParticipantModerator(state);

    return {
        overflowDrawer,
        _sharingWhiteBoard: sharingWhiteBoard,
        _screenShareDisabled: screenShareDisabledForGuest && !isPersonModerator,
        _shareEtherpad: shareEtherpad,
        _conferenceView: conferenceView
    };
}

export default translate(connect(mapStateToProps)(ScreenOverflowButton));
